import { db } from './config';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { convertBaseballInningsToDecimal } from '../utils/calculations';

/**
 * User Management Functions
 */

/**
 * Initialize a new user document in Firestore when they sign up
 * @param {string} uid - User ID from Firebase Auth
 * @param {object} userData - User data to store
 * @returns {Promise<void>}
 */
export const initializeUser = async (uid, userData) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnapshot = await getDoc(userRef);
    
    // Only create a new user document if it doesn't already exist
    if (!userSnapshot.exists()) {
      await setDoc(userRef, {
        displayName: userData.displayName || '',
        email: userData.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    return true;
  } catch (error) {
    console.error('Error initializing user:', error);
    throw error;
  }
};
export const updateUserData = async (uid, userData) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

/**
 * Player Management Functions
 */

/**
 * Create a new game with validated data
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {object} gameData - Game data
 * @returns {Promise<string>} - ID of the created game
 */
export const createGame = async (uid, playerId, gameData) => {
  try {
    console.log('[STATS DEBUG] Creating new game for player:', playerId);
    console.log('[STATS DEBUG] Full game data:', JSON.stringify(gameData, null, 2));
    
    // Validate game data
    if (!gameData) {
      console.error('[STATS DEBUG] Invalid game data provided');
      throw new Error('Invalid game data');
    }
    
    // Ensure numeric values for batting stats
    const processedBattingStats = gameData.battingStats ? {
      atBats: Number(gameData.battingStats.atBats || 0),
      hits: Number(gameData.battingStats.hits || 0),
      doubles: Number(gameData.battingStats.doubles || 0),
      triples: Number(gameData.battingStats.triples || 0),
      homeRuns: Number(gameData.battingStats.homeRuns || 0),
      runs: Number(gameData.battingStats.runs || 0),
      rbi: Number(gameData.battingStats.rbi || 0),
      walks: Number(gameData.battingStats.walks || 0),
      strikeouts: Number(gameData.battingStats.strikeouts || 0),
      stolenBases: Number(gameData.battingStats.stolenBases || 0),
      hitByPitch: Number(gameData.battingStats.hitByPitch || 0),
      sacrificeFlies: Number(gameData.battingStats.sacrificeFlies || 0)
    } : null;
    
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    
    const gameWithMetadata = {
      ...gameData,
      battingStats: processedBattingStats,  // Use processed stats
      seasonId: gameData.seasonId,
      date: gameData.date || new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    console.log('[STATS DEBUG] Saving game with processed data:', {
      seasonId: gameWithMetadata.seasonId,
      date: gameWithMetadata.date,
      battingStats: processedBattingStats ? {
        atBats: processedBattingStats.atBats,
        hits: processedBattingStats.hits
      } : null
    });
    
    const docRef = await addDoc(gameCollectionRef, gameWithMetadata);
    
    // Verify the game was saved correctly
    const savedGame = await getDoc(docRef);
    console.log('[STATS DEBUG] Game saved successfully:', {
      id: docRef.id,
      data: savedGame.data()
    });
    
    // After adding a game, update only the season stats
    if (gameData.seasonId) {
      console.log(`[STATS DEBUG] Updating season stats after adding game to season ${gameData.seasonId}`);
      await updateSeasonPlayerStats(uid, playerId, gameData.seasonId);
      try {
        await updateSeasonPlayerStats(uid, playerId, gameData.seasonId);
        
        // Also get all current games for this season to verify
        const seasonGames = await getSeasonGames(uid, playerId, gameData.seasonId);
        console.log(`[STATS DEBUG] After adding game, season now has ${seasonGames.length} games`);
        
        // Get the updated stats to verify
        const seasonStats = await getSeasonStats(uid, playerId, gameData.seasonId);
        console.log('[STATS DEBUG] Updated season stats:', {
          battingStats: {
            games: seasonStats?.battingStats?.games || 0,
            atBats: seasonStats?.battingStats?.atBats || 0,
            hits: seasonStats?.battingStats?.hits || 0,
            average: seasonStats?.battingStats?.average || 0
          }
        });
      } catch (statsError) {
        console.error('[STATS DEBUG] Error updating stats after adding game:', statsError);
        // Don't throw since the game was still created successfully
      }
    } else {
      console.log('[STATS DEBUG] Game has no seasonId, skipping season stats update');
    }
  } catch (error) {
    console.error('[STATS DEBUG] Error creating game:', error);
    throw error;
  }
};
/**
 * Create a new player
 * @param {string} uid - User ID from Firebase Auth
 * @param {object} playerData - Player data
 * @returns {Promise<string>} - ID of the created player
 */
export const createPlayer = async (uid, playerData) => {
  try {
    console.log('Creating player with data:', JSON.stringify(playerData, null, 2));
    console.log('User ID:', uid);
    
    // Validate required fields
    if (!uid) {
      throw new Error('User ID is required to create a player');
    }
    
    if (!playerData.name) {
      throw new Error('Player name is required');
    }
    
    // Ensure leagues is an array
    if (!playerData.leagues || !Array.isArray(playerData.leagues)) {
      console.warn('Leagues is not an array, setting default');
      playerData.leagues = ['Recreation League'];
    }
    
    const playerCollectionRef = collection(db, 'users', uid, 'players');
    
    const playerWithStats = {
      ...playerData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      battingStats: {
        average: 0,
        obp: 0,
        slg: 0,
        ops: 0,
        atBats: 0,
        hits: 0,
        doubles: 0,
        triples: 0,
        homeRuns: 0,
        runs: 0,
        rbi: 0,
        walks: 0,
        strikeouts: 0,
        stolenBases: 0,
        hitByPitch: 0,
        sacrificeFlies: 0,
        games: 0
      },
      pitchingStats: {
        era: 0,
        whip: 0,
        wins: 0,
        losses: 0,
        inningsPitched: 0,
        strikeouts: 0,
        walks: 0,
        hits: 0,
        earnedRuns: 0,
        games: 0
      }
    };
    
    console.log('Saving player with processed data:', JSON.stringify({
      name: playerWithStats.name,
      leagues: playerWithStats.leagues,
      position: playerWithStats.position
    }, null, 2));
    
    try {
      const docRef = await addDoc(playerCollectionRef, playerWithStats);
      console.log('Player created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (firestoreError) {
      console.error('Firestore error creating player:', firestoreError);
      console.error('Error code:', firestoreError.code);
      console.error('Error message:', firestoreError.message);
      throw new Error(`Firestore error: ${firestoreError.message}`);
    }
  } catch (error) {
    console.error('Error creating player:', error);
    throw error;
  }
};

/**
 * Get a player by ID
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<object|null>} - Player data or null if not found
 */
export const getPlayer = async (uid, playerId) => {
  try {
    const playerRef = doc(db, 'users', uid, 'players', playerId);
    const playerSnapshot = await getDoc(playerRef);
    
    if (playerSnapshot.exists()) {
      return { id: playerSnapshot.id, ...playerSnapshot.data() };
    } else {
      console.log('No player found!');
      return null;
    }
  } catch (error) {
    console.error('Error getting player:', error);
    throw error;
  }
};

/**
 * Get all players for a user
 * @param {string} uid - User ID from Firebase Auth
 * @returns {Promise<Array>} - Array of player objects
 */
export const getPlayers = async (uid) => {
  try {
    const playerCollectionRef = collection(db, 'users', uid, 'players');
    const querySnapshot = await getDocs(playerCollectionRef);
    
    const players = [];
    querySnapshot.forEach((doc) => {
      players.push({ id: doc.id, ...doc.data() });
    });
    
    return players;
  } catch (error) {
    console.error('Error getting players:', error);
    throw error;
  }
};

/**
 * Update a player profile
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {object} playerData - Player data to update
 * @returns {Promise<void>}
 */
export const updatePlayer = async (uid, playerId, playerData) => {
  try {
    const playerRef = doc(db, 'users', uid, 'players', playerId);
    
    // Don't override stats when updating player profile
    const { battingStats, pitchingStats, ...updateData } = playerData;
    
    await updateDoc(playerRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating player:', error);
    throw error;
  }
};

/**
 * Delete a player profile
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<void>}
 */
export const deletePlayer = async (uid, playerId) => {
  try {
    const playerRef = doc(db, 'users', uid, 'players', playerId);
    await deleteDoc(playerRef);
  } catch (error) {
    console.error('Error deleting player:', error);
    throw error;
  }
};

/**
 * Game Management Functions
 */

// Note: The createGame function implementation is already defined at line 74


/**
 * Get all games for a player
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<Array>} - Array of game objects
 */
export const getGames = async (uid, playerId) => {
  try {
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const q = query(gameCollectionRef, orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const games = [];
    querySnapshot.forEach((doc) => {
      games.push({ id: doc.id, ...doc.data() });
    });
    
    return games;
  } catch (error) {
    console.error('Error getting games:', error);
    throw error;
  }
};

/**
 * Get recent games for a player
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {number} count - Number of games to retrieve
 * @returns {Promise<Array>} - Array of game objects
 */
export const getRecentGames = async (uid, playerId, count = 5) => {
  try {
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const q = query(gameCollectionRef, orderBy('date', 'desc'), limit(count));
    const querySnapshot = await getDocs(q);
    
    const games = [];
    querySnapshot.forEach((doc) => {
      games.push({ id: doc.id, ...doc.data() });
    });
    
    return games;
  } catch (error) {
    console.error('Error getting recent games:', error);
    throw error;
  }
};
/**
 * Get all games for a player (ordered by date)
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<Array>} - Array of game objects
 */
// Implementation moved to line 288

/**
 * Delete a game
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} gameId - Game ID
 * @returns {Promise<void>}
 */
export const deleteGame = async (uid, playerId, gameId) => {
  try {
    // Get the game's seasonId before deleting
    const gameRef = doc(db, 'users', uid, 'players', playerId, 'games', gameId);
    const gameSnapshot = await getDoc(gameRef);
    const seasonId = gameSnapshot.exists() ? gameSnapshot.data().seasonId : null;
    
    await deleteDoc(gameRef);
    
    // After deleting a game, update only the season stats
    if (seasonId) {
      await updateSeasonPlayerStats(uid, playerId, seasonId);
    }
  } catch (error) {
    console.error('Error deleting game:', error);
    throw error;
  }
};

/**
 * Stats Calculation Utilities
 */

/**
 * Calculate batting statistics from game data
 * @param {Array} games - Array of game objects
 * @returns {object} - Calculated batting stats
 */
export const calculateBattingStats = (games) => {
  console.log(`[STATS DEBUG] Starting batting stats calculation with ${games?.length || 0} games`);
  
  // Import utility functions from calculations.js
  const { 
    ensureNumber, 
    calculateBattingAverage, 
    calculateTotalBases, 
    calculateOBP, 
    calculateSLG, 
    calculateOPS 
  } = require('../utils/calculations');
  
  // Initialize stats with zeros
  const stats = {
    average: 0,
    obp: 0,
    slg: 0,
    ops: 0,
    atBats: 0,
    hits: 0,
    doubles: 0,
    triples: 0,
    homeRuns: 0,
    runs: 0,
    rbi: 0, // Standardize to rbi instead of rbis
    walks: 0,
    strikeouts: 0,
    stolenBases: 0,
    hitByPitch: 0,
    sacrificeFlies: 0,
    games: 0,
    totalBases: 0
  };
  
  if (!games || games.length === 0) {
    console.log('[STATS DEBUG] No games provided for stats calculation');
    return stats;
  }

  // Dump full game data to console for the first few games
  console.log('[STATS DEBUG] Sample game data (first 2 games):', 
    JSON.stringify(games.slice(0, 2).map(g => ({
      id: g.id,
      date: g.date,
      seasonId: g.seasonId,
      battingStats: g.battingStats
    })), null, 2)
  );
  
  console.log('[STATS DEBUG] Game IDs being processed:', games.map(g => g.id).join(', '));
  
  // Validate game structure before processing
  const invalidGames = games.filter(game => !game.battingStats);
  if (invalidGames.length > 0) {
    console.warn(`[STATS DEBUG] Found ${invalidGames.length} games without batting stats!`, 
      invalidGames.map(g => ({ id: g.id, date: g.date })));
  }
  
  // Track running totals for validation
  let runningTotal = { atBats: 0, hits: 0 };
  
  // Sum up all stats from games with strict numeric conversion
  games.forEach((game, index) => {
    if (game.battingStats) {
      stats.games++;
      
      // Validate game stats numeric format before conversion
      const rawAtBats = game.battingStats.atBats;
      const rawHits = game.battingStats.hits;
      
      // Check for non-numeric values
      if (isNaN(rawAtBats)) {
        console.warn(`[STATS DEBUG] Game ${game.id} has non-numeric atBats: ${rawAtBats}`);
      }
      if (isNaN(rawHits)) {
        console.warn(`[STATS DEBUG] Game ${game.id} has non-numeric hits: ${rawHits}`);
      }
      
      // Convert to numbers with safeguards
      const gameStats = {
        atBats: ensureNumber(game.battingStats.atBats),
        hits: ensureNumber(game.battingStats.hits),
        doubles: ensureNumber(game.battingStats.doubles),
        triples: ensureNumber(game.battingStats.triples),
        homeRuns: ensureNumber(game.battingStats.homeRuns),
        walks: ensureNumber(game.battingStats.walks),
        hitByPitch: ensureNumber(game.battingStats.hitByPitch),
        sacrificeFlies: ensureNumber(game.battingStats.sacrificeFlies)
      };
      
      // Validate game stats for logical consistency
      if (gameStats.hits > gameStats.atBats) {
        console.warn(`[STATS DEBUG] Game ${game.id} has more hits (${gameStats.hits}) than at-bats (${gameStats.atBats})`);
      }
      
      if (gameStats.doubles + gameStats.triples + gameStats.homeRuns > gameStats.hits) {
        console.warn(`[STATS DEBUG] Game ${game.id} has more extra-base hits than total hits`);
      }
      
      // Update running totals
      runningTotal.atBats += gameStats.atBats;
      runningTotal.hits += gameStats.hits;
      
      // Add to overall stats
      stats.atBats += gameStats.atBats;
      stats.hits += gameStats.hits;
      stats.doubles += ensureNumber(game.battingStats.doubles);
      stats.triples += ensureNumber(game.battingStats.triples);
      stats.homeRuns += ensureNumber(game.battingStats.homeRuns);
      stats.runs += ensureNumber(game.battingStats.runs);
      stats.rbi += ensureNumber(game.battingStats.rbi);
      stats.walks += ensureNumber(game.battingStats.walks);
      stats.strikeouts += ensureNumber(game.battingStats.strikeouts);
      stats.stolenBases += ensureNumber(game.battingStats.stolenBases);
      stats.hitByPitch += ensureNumber(game.battingStats.hitByPitch);
      stats.sacrificeFlies += ensureNumber(game.battingStats.sacrificeFlies);
      
      // Debug log for significant game contributions
      if (gameStats.hits > 0 || gameStats.atBats > 0) {
        console.log(`[STATS DEBUG] Game ${game.id} contributes: AB=${gameStats.atBats}, H=${gameStats.hits}, 2B=${gameStats.doubles}, 3B=${gameStats.triples}, HR=${gameStats.homeRuns}`);
      }
    }
  });
  
  // Check if running total matches final totals
  if (runningTotal.atBats !== stats.atBats || runningTotal.hits !== stats.hits) {
    console.error('[STATS DEBUG] Aggregation error: Running totals do not match final totals', {
      running: runningTotal,
      final: { atBats: stats.atBats, hits: stats.hits }
    });
  }

  // Calculate derived stats using utility functions
  if (stats.atBats > 0) {
    // Calculate batting average
    stats.average = calculateBattingAverage(stats.hits, stats.atBats);
    
    // Calculate total bases
    stats.totalBases = calculateTotalBases(
      stats.hits,
      stats.doubles,
      stats.triples,
      stats.homeRuns
    );
    
    // Calculate slugging percentage
    stats.slg = calculateSLG(stats.totalBases, stats.atBats);
    
    // Calculate on-base percentage
    stats.obp = calculateOBP(
      stats.hits,
      stats.walks,
      stats.hitByPitch,
      stats.atBats,
      stats.sacrificeFlies
    );
    
    // Calculate OPS
    stats.ops = calculateOPS(stats.obp, stats.slg);
    
    // Validate stats relationships
    console.log('[STATS DEBUG] Stats validation:', {
      totalBases: stats.totalBases,
      avgCheck: calculateBattingAverage(stats.hits, stats.atBats),
      obpCheck: calculateOBP(
        stats.hits,
        stats.walks,
        stats.hitByPitch,
        stats.atBats,
        stats.sacrificeFlies
      ),
      slgCheck: calculateSLG(stats.totalBases, stats.atBats),
      opsCheck: calculateOPS(stats.obp, stats.slg)
    });
  }
  
  // Debug log final results
  console.log('[STATS DEBUG] Final calculated stats:', {
    games: stats.games,
    atBats: stats.atBats,
    hits: stats.hits,
    doubles: stats.doubles,
    triples: stats.triples,
    homeRuns: stats.homeRuns,
    average: stats.average,
    obp: stats.obp,
    slg: stats.slg,
    ops: stats.ops,
    totalBases: stats.totalBases
  });
  
  return stats;
};

/**
 * Calculate pitching statistics from game data
 * @param {Array} games - Array of game objects
 * @returns {object} - Calculated pitching stats
 */
export const calculatePitchingStats = (games) => {
  const stats = {
    era: 0,
    whip: 0,
    wins: 0,
    losses: 0,
    inningsPitched: 0,
    strikeouts: 0,
    walks: 0,
    hits: 0,
    earnedRuns: 0,
    saves: 0,
    games: 0,
    gamesStarted: 0,
    completeGames: 0
  };
  
  if (!games || games.length === 0) return stats;
  
  // Sum up all stats from games
  let totalInningsDecimal = 0; // Track innings in decimal form for accurate summation

  games.forEach(game => {
    if (game.pitchingStats) {
      stats.wins += game.pitchingStats.win ? 1 : 0;
      stats.losses += game.pitchingStats.loss ? 1 : 0;

      // Convert baseball innings notation to decimal before adding
      const gameInnings = game.pitchingStats.inningsPitched || 0;
      totalInningsDecimal += convertBaseballInningsToDecimal(gameInnings);

      stats.strikeouts += game.pitchingStats.strikeouts || 0;
      stats.walks += game.pitchingStats.walks || 0;
      stats.hits += game.pitchingStats.hits || 0;
      stats.earnedRuns += game.pitchingStats.earnedRuns || 0;
      stats.saves += game.pitchingStats.save ? 1 : 0;
      stats.games += 1;
      stats.gamesStarted += game.pitchingStats.started ? 1 : 0;
      stats.completeGames += game.pitchingStats.completeGame ? 1 : 0;
    }
  });

  // Convert decimal innings back to baseball notation
  // E.g., 13.333... becomes 13.1 (13 innings + 1 out)
  const wholeInnings = Math.floor(totalInningsDecimal);
  const fractionalInnings = totalInningsDecimal - wholeInnings;
  const outs = Math.round(fractionalInnings * 3); // Convert fraction to outs (0, 1, or 2)
  stats.inningsPitched = wholeInnings + (outs / 10); // Convert back to baseball notation
  
  // Calculate derived stats using decimal innings for accuracy
  if (totalInningsDecimal > 0) {
    // ERA = (Earned Runs / Innings Pitched) * 9
    // Use decimal innings for accurate calculation
    stats.era = (stats.earnedRuns / totalInningsDecimal) * 9;

    // WHIP = (Walks + Hits) / Innings Pitched
    // Use decimal innings for accurate calculation
    stats.whip = (stats.walks + stats.hits) / totalInningsDecimal;
  }

  console.log('[STATS DEBUG] Pitching stats calculated:', {
    games: stats.games,
    inningsPitched: stats.inningsPitched,
    inningsDecimal: totalInningsDecimal.toFixed(3),
    earnedRuns: stats.earnedRuns,
    walks: stats.walks,
    hits: stats.hits,
    era: stats.era.toFixed(2),
    whip: stats.whip.toFixed(2)
  });

  return stats;
};

/**
 * Update a player's stats based on their game data
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<void>}
 */

/**
 * Update a player's career stats
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {object} statsData - Stats data to update
 * @returns {Promise<void>}
 */
export const updatePlayerCareerStats = async (uid, playerId, statsData) => {
  try {
    const playerRef = doc(db, 'users', uid, 'players', playerId);
    
    await updateDoc(playerRef, {
      ...statsData,
      updatedAt: serverTimestamp(),
      lastStatsUpdate: new Date().toISOString()
    });
    
    console.log('Player career stats updated successfully');
  } catch (error) {
    console.error('Error updating player career stats:', error);
    throw error;
  }
};

/**
 * Scheduled Games Management Functions
 */

/**
 * Create a new scheduled game
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {object} gameData - Scheduled game data
 * @returns {Promise<string>} - ID of the created scheduled game
 */
export const createScheduledGame = async (uid, playerId, gameData) => {
  try {
    console.log('Creating scheduled game:', { uid, playerId, gameData });
    const scheduledGameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'scheduledGames');
    
    const gameWithMetadata = {
      ...gameData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'upcoming'
    };
    
    console.log('Saving game with metadata:', gameWithMetadata);
    const docRef = await addDoc(scheduledGameCollectionRef, gameWithMetadata);
    console.log('Game saved successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error in createScheduledGame:', error);
    throw error;
  }
};

/**
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<Array>} - Array of scheduled game objects
 */
export const getScheduledGames = async (uid, playerId) => {
  try {
    console.log('Getting scheduled games for:', { uid, playerId });
    const scheduledGameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'scheduledGames');
    const q = query(scheduledGameCollectionRef, orderBy('date', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const games = [];
    querySnapshot.forEach((doc) => {
      games.push({ id: doc.id, ...doc.data() });
    });
    
    console.log('Retrieved scheduled games:', games);
    return games;
  } catch (error) {
    console.error('Error getting scheduled games:', error);
    throw error;
  }
};

/**
 * Update a scheduled game
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} gameId - Scheduled game ID
 * @param {object} gameData - Game data to update
 * @returns {Promise<void>}
 */
export const updateScheduledGame = async (uid, playerId, gameId, gameData) => {
  try {
    const gameRef = doc(db, 'users', uid, 'players', playerId, 'scheduledGames', gameId);
    
    await updateDoc(gameRef, {
      ...gameData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating scheduled game:', error);
    throw error;
  }
};

/**
 * Delete a scheduled game
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} gameId - Scheduled game ID
 * @returns {Promise<void>}
 */
export const deleteScheduledGame = async (uid, playerId, gameId) => {
  try {
    console.log('Deleting scheduled game:', { uid, playerId, gameId });
    const gameRef = doc(db, 'users', uid, 'players', playerId, 'scheduledGames', gameId);
    await deleteDoc(gameRef);
    console.log('Successfully deleted scheduled game:', gameId);
  } catch (error) {
    console.error('Error deleting scheduled game:', error);
    throw error;
  }
};

/**
 * Season Management Functions
 */

/**
 * Create a new season
 * @param {string} uid - User ID from Firebase Auth
 * @param {object} seasonData - Season data
 * @returns {Promise<string>} - ID of the created season
 */
export const createSeason = async (uid, seasonData) => {
  try {
    const seasonCollectionRef = collection(db, 'users', uid, 'seasons');
    
    const seasonWithMetadata = {
      ...seasonData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: seasonData.isActive || false
    };
    
    const docRef = await addDoc(seasonCollectionRef, seasonWithMetadata);
    
    // If this is the first season or it's marked as active, set it as the active season
    if (seasonData.isActive) {
      await setActiveSeason(uid, docRef.id);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating season:', error);
    throw error;
  }
};

/**
 * Get a season by ID
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} seasonId - Season ID
 * @returns {Promise<object|null>} - Season data or null if not found
 */
export const getSeason = async (uid, seasonId) => {
  try {
    const seasonRef = doc(db, 'users', uid, 'seasons', seasonId);
    const seasonSnapshot = await getDoc(seasonRef);
    
    if (seasonSnapshot.exists()) {
      return { id: seasonSnapshot.id, ...seasonSnapshot.data() };
    } else {
      console.log('No season found!');
      return null;
    }
  } catch (error) {
    console.error('Error getting season:', error);
    throw error;
  }
};

/**
 * Get all seasons for a user
 * @param {string} uid - User ID from Firebase Auth
 * @returns {Promise<Array>} - Array of season objects
 */
export const getSeasons = async (uid) => {
  try {
    const seasonCollectionRef = collection(db, 'users', uid, 'seasons');
    const querySnapshot = await getDocs(seasonCollectionRef);
    
    const seasons = [];
    querySnapshot.forEach((doc) => {
      seasons.push({ id: doc.id, ...doc.data() });
    });
    
    return seasons;
  } catch (error) {
    console.error('Error getting seasons:', error);
    throw error;
  }
};

/**
 * Update a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} seasonId - Season ID
 * @param {object} seasonData - Season data to update
 * @returns {Promise<void>}
 */
export const updateSeason = async (uid, seasonId, seasonData) => {
  try {
    const seasonRef = doc(db, 'users', uid, 'seasons', seasonId);
    
    await updateDoc(seasonRef, {
      ...seasonData,
      updatedAt: serverTimestamp()
    });
    
    // If season is being set as active, update active season
    if (seasonData.isActive) {
      await setActiveSeason(uid, seasonId);
    }
  } catch (error) {
    console.error('Error updating season:', error);
    throw error;
  }
};

/**
 * Set a season as the active season (deactivate others)
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} seasonId - Season ID to set as active
 * @returns {Promise<void>}
 */
export const setActiveSeason = async (uid, seasonId) => {
  try {
    const seasonCollectionRef = collection(db, 'users', uid, 'seasons');
    const querySnapshot = await getDocs(seasonCollectionRef);
    
    const batch = writeBatch(db);
    
    // Update all seasons to be inactive
    querySnapshot.forEach((doc) => {
      if (doc.id !== seasonId) {
        const seasonRef = doc.ref;
        batch.update(seasonRef, { isActive: false });
      }
    });
    
    // Set the specified season as active
    const activeSeasonRef = doc(db, 'users', uid, 'seasons', seasonId);
    batch.update(activeSeasonRef, { isActive: true });
    
    // Commit the batch
    await batch.commit();
  } catch (error) {
    console.error('Error setting active season:', error);
    throw error;
  }
};

/**
 * Get the active season for a user
 * @param {string} uid - User ID from Firebase Auth
 * @returns {Promise<object|null>} - Active season data or null if none is active
 */
export const getActiveSeason = async (uid) => {
  try {
    const seasonCollectionRef = collection(db, 'users', uid, 'seasons');
    const q = query(seasonCollectionRef, where('isActive', '==', true), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } else {
      // If no active season, get all seasons and return the most recent
      const seasons = await getSeasons(uid);
      if (seasons.length > 0) {
        // Sort by createdAt (newest first)
        seasons.sort((a, b) => {
          const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
          return dateB - dateA;
        });
        return seasons[0];
      }
      return null;
    }
  } catch (error) {
    console.error('Error getting active season:', error);
    throw error;
  }
};

/**
 * Delete a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} seasonId - Season ID
 * @returns {Promise<void>}
 */
export const deleteSeason = async (uid, seasonId) => {
  try {
    const seasonRef = doc(db, 'users', uid, 'seasons', seasonId);
    const seasonSnapshot = await getDoc(seasonRef);
    
    if (seasonSnapshot.exists() && seasonSnapshot.data().isActive) {
      // If deleting the active season, find another to make active
      const seasons = await getSeasons(uid);
      const otherSeasons = seasons.filter(season => season.id !== seasonId);
      
      if (otherSeasons.length > 0) {
        // Set another season as active before deleting this one
        await setActiveSeason(uid, otherSeasons[0].id);
      }
    }
    
    await deleteDoc(seasonRef);
  } catch (error) {
    console.error('Error deleting season:', error);
    throw error;
  }
};
/**
 * Update player stats for a specific season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<void>}
 */
export const updateSeasonPlayerStats = async (uid, playerId, seasonId) => {
  try {
    console.log(`[STATS DEBUG] ===== Begin updateSeasonPlayerStats for player ${playerId}, season ${seasonId} =====`);
    
    if (!uid || !playerId || !seasonId) {
      console.error('[STATS DEBUG] Missing required parameters:', { uid, playerId, seasonId });
      throw new Error('Missing required parameters for updateSeasonPlayerStats');
    }
    
    // Get games specifically for this season
    console.log(`[STATS DEBUG] Fetching games for season ${seasonId}`);
    const seasonGames = await getSeasonGames(uid, playerId, seasonId);
    
    console.log(`[STATS DEBUG] Calculating stats for ${seasonGames.length} games in season ${seasonId}`);
    
    // List all games with their dates and stats
    if (seasonGames.length > 0) {
      console.log('[STATS DEBUG] First few games in the season:');
      seasonGames.slice(0, 3).forEach((game, index) => {
        console.log(`[STATS DEBUG] Game ${index + 1}: ${game.id}`, {
          date: game.date,
          opponent: game.opponent,
          battingStats: game.battingStats ? {
            atBats: game.battingStats.atBats,
            hits: game.battingStats.hits
          } : 'No batting stats'
        });
      });
      
      // Show totals for validation
      const totalAtBats = seasonGames.reduce((sum, game) => 
        sum + Number(game.battingStats?.atBats || 0), 0);
      const totalHits = seasonGames.reduce((sum, game) => 
        sum + Number(game.battingStats?.hits || 0), 0);
      
      console.log('[STATS DEBUG] Season game totals:', {
        games: seasonGames.length,
        totalAtBats,
        totalHits,
        expectedAverage: totalAtBats > 0 ? (totalHits / totalAtBats).toFixed(3) : '0.000'
      });
    } else {
      console.log('[STATS DEBUG] No games found for this season');
    }
    
    // Calculate stats for the season
    const seasonBattingStats = calculateBattingStats(seasonGames);
    const seasonPitchingStats = calculatePitchingStats(seasonGames);

    console.log('[STATS DEBUG] Calculated season batting stats:', {
      games: seasonBattingStats.games,
      atBats: seasonBattingStats.atBats,
      hits: seasonBattingStats.hits,
      average: seasonBattingStats.average,
      rbi: seasonBattingStats.rbi,
      homeRuns: seasonBattingStats.homeRuns
    });
    
    // Get all games for career stats (including this season)
    console.log('[STATS DEBUG] Fetching all games for career stats calculation');
    const allGames = await getGames(uid, playerId);
    
    if (allGames.length > 0) {
      console.log(`[STATS DEBUG] Found ${allGames.length} total games for career stats`);
      
      // Calculate career stats
      const careerBattingStats = calculateBattingStats(allGames);
      const careerPitchingStats = calculatePitchingStats(allGames);

      console.log('[STATS DEBUG] Calculated career batting stats:', {
        games: careerBattingStats.games,
        atBats: careerBattingStats.atBats,
        hits: careerBattingStats.hits,
        average: careerBattingStats.average,
        rbi: careerBattingStats.rbi,
        homeRuns: careerBattingStats.homeRuns
      });
      
      // Validate stats BEFORE transaction to prevent bad data from being written
      if (seasonBattingStats.atBats > 0) {
        const expectedSeasonAvg = seasonBattingStats.hits / seasonBattingStats.atBats;
        if (Math.abs(expectedSeasonAvg - seasonBattingStats.average) > 0.001) {
          const error = new Error('Season stats validation failed: batting average mismatch');
          console.error('[STATS DEBUG] Season average calculation error!', {
            calculated: seasonBattingStats.average,
            expected: expectedSeasonAvg,
            hits: seasonBattingStats.hits,
            atBats: seasonBattingStats.atBats
          });
          throw error;
        }
      }

      if (careerBattingStats.atBats > 0) {
        const expectedCareerAvg = careerBattingStats.hits / careerBattingStats.atBats;
        if (Math.abs(expectedCareerAvg - careerBattingStats.average) > 0.001) {
          const error = new Error('Career stats validation failed: batting average mismatch');
          console.error('[STATS DEBUG] Career average calculation error!', {
            calculated: careerBattingStats.average,
            expected: expectedCareerAvg,
            hits: careerBattingStats.hits,
            atBats: careerBattingStats.atBats
          });
          throw error;
        }
      }

      // Use transaction to update both season and career stats atomically
      await runTransaction(db, async (transaction) => {
        console.log('[STATS DEBUG] Starting atomic transaction for stats update');

        // Set season stats
        const statsRef = doc(db, 'users', uid, 'players', playerId, 'seasons', seasonId);
        transaction.set(statsRef, {
          battingStats: seasonBattingStats,
          pitchingStats: seasonPitchingStats,
          updatedAt: serverTimestamp()
        });

        // Update career stats in player document
        const playerRef = doc(db, 'users', uid, 'players', playerId);
        transaction.update(playerRef, {
          battingStats: careerBattingStats,
          pitchingStats: careerPitchingStats,
          updatedAt: serverTimestamp(),
          lastStatsUpdate: new Date().toISOString()
        });

        console.log('[STATS DEBUG] Transaction prepared and validated, committing...');
      });
      
      // Verify the updates were successful by fetching the latest data
      const playerRef = doc(db, 'users', uid, 'players', playerId);
      const statsRef = doc(db, 'users', uid, 'players', playerId, 'seasons', seasonId);
      
      const [playerDoc, statsDoc] = await Promise.all([
        getDoc(playerRef),
        getDoc(statsRef)
      ]);
      
      if (playerDoc.exists() && statsDoc.exists()) {
        const careerStats = playerDoc.data().battingStats;
        const seasonStats = statsDoc.data().battingStats;

        console.log('[STATS DEBUG] Verification - updated career stats:', {
          games: careerStats?.games || 0,
          atBats: careerStats?.atBats || 0,
          hits: careerStats?.hits || 0,
          average: careerStats?.average || 0
        });

        console.log('[STATS DEBUG] Verification - updated season stats:', {
          games: seasonStats?.games || 0,
          atBats: seasonStats?.atBats || 0,
          hits: seasonStats?.hits || 0,
          average: seasonStats?.average || 0
        });

        // Data integrity check: Verify career stats >= season stats
        const integrityIssues = [];
        if (careerStats.atBats < seasonStats.atBats) {
          integrityIssues.push(`Career at-bats (${careerStats.atBats}) < season at-bats (${seasonStats.atBats})`);
        }
        if (careerStats.hits < seasonStats.hits) {
          integrityIssues.push(`Career hits (${careerStats.hits}) < season hits (${seasonStats.hits})`);
        }
        if (careerStats.games < seasonStats.games) {
          integrityIssues.push(`Career games (${careerStats.games}) < season games (${seasonStats.games})`);
        }

        if (integrityIssues.length > 0) {
          console.error('[STATS DEBUG] ⚠️ DATA INTEGRITY ISSUES DETECTED:', integrityIssues);
          throw new Error(`Data integrity check failed: ${integrityIssues.join('; ')}`);
        } else {
          console.log('[STATS DEBUG] ✅ Data integrity check passed');
        }
      }
      console.log('[STATS DEBUG] Updated both career and season stats in a single transaction');
    } else {
      // If no games found for career stats, still update season stats
      console.log('[STATS DEBUG] No games found for career stats, updating season stats only');
      
      // Just update season stats
      const statsRef = doc(db, 'users', uid, 'players', playerId, 'seasons', seasonId);
      await setDoc(statsRef, {
        battingStats: seasonBattingStats,
        pitchingStats: seasonPitchingStats,
        updatedAt: serverTimestamp()
      });
      
      // Retrieve the saved stats to confirm they were saved correctly
      const statsDoc = await getDoc(statsRef);
      if (statsDoc.exists()) {
        console.log(`[STATS DEBUG] Verified stats saved for season ${seasonId}:`, {
          battingStats: {
            games: statsDoc.data().battingStats?.games || 0,
            atBats: statsDoc.data().battingStats?.atBats || 0,
            hits: statsDoc.data().battingStats?.hits || 0,
            average: statsDoc.data().battingStats?.average || 0
          }
        });
      } else {
        console.error(`[STATS DEBUG] Stats document does not exist after saving for season ${seasonId}`);
      }
    }
    
    console.log(`[STATS DEBUG] Player stats updated successfully for season ${seasonId}`);
    console.log(`[STATS DEBUG] ===== End updateSeasonPlayerStats =====`);
  } catch (error) {
    console.error('[STATS DEBUG] Error updating season player stats:', error);
    throw error;
  }
};

/**
 * Recalculate career stats from scratch for a player
 * This fixes any incorrectly calculated stats from old formulas
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @returns {Promise<object>} - Updated career stats
 */
export const recalculateCareerStats = async (uid, playerId) => {
  console.log(`[STATS DEBUG] ===== Recalculating career stats for player ${playerId} =====`);

  try {
    // Get ALL games for the player (regardless of season)
    const gamesCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const gamesQuery = query(gamesCollectionRef, orderBy('date', 'desc'));
    const gamesSnapshot = await getDocs(gamesQuery);

    const allGames = [];
    gamesSnapshot.forEach((doc) => {
      allGames.push({ id: doc.id, ...doc.data() });
    });

    console.log(`[STATS DEBUG] Found ${allGames.length} total games for career stats`);

    // Calculate career stats using the CORRECTED calculation functions
    const careerBattingStats = calculateBattingStats(allGames);
    const careerPitchingStats = calculatePitchingStats(allGames);

    console.log('[STATS DEBUG] Recalculated career batting stats:', {
      games: careerBattingStats.games,
      atBats: careerBattingStats.atBats,
      hits: careerBattingStats.hits,
      average: careerBattingStats.average,
      homeRuns: careerBattingStats.homeRuns,
      rbi: careerBattingStats.rbi
    });

    console.log('[STATS DEBUG] Recalculated career pitching stats:', {
      games: careerPitchingStats.games,
      inningsPitched: careerPitchingStats.inningsPitched,
      earnedRuns: careerPitchingStats.earnedRuns,
      era: careerPitchingStats.era.toFixed(2),
      whip: careerPitchingStats.whip.toFixed(2),
      strikeouts: careerPitchingStats.strikeouts
    });

    // Update the player document with corrected career stats
    const playerRef = doc(db, 'users', uid, 'players', playerId);
    await updateDoc(playerRef, {
      battingStats: careerBattingStats,
      pitchingStats: careerPitchingStats,
      updatedAt: serverTimestamp(),
      lastStatsRecalculation: new Date().toISOString()
    });

    console.log('[STATS DEBUG] ✅ Career stats successfully recalculated and updated');

    return {
      battingStats: careerBattingStats,
      pitchingStats: careerPitchingStats
    };
  } catch (error) {
    console.error('[STATS DEBUG] Error recalculating career stats:', error);
    throw error;
  }
};

/**
 * Migrate existing games to a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID to migrate games to
 * @returns {Promise<number>} - Number of games migrated
 */
export const migrateGamesToSeason = async (uid, playerId, seasonId) => {
  try {
    // Get all games for the player that are not yet associated with a season
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const q = query(gameCollectionRef, where('seasonId', '==', null));
    const querySnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    let count = 0;
    
    querySnapshot.forEach((gameDoc) => {
      const gameRef = gameDoc.ref;
      batch.update(gameRef, { seasonId: seasonId });
      count++;
    });
    
    if (count > 0) {
      await batch.commit();
    }
    
    return count;
  } catch (error) {
    console.error('Error migrating games to season:', error);
    throw error;
  }
};

/**
 * Season-Aware Game Management Functions
 */

/**
 * Create a new game associated with a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID
 * @param {object} gameData - Game data
 * @returns {Promise<string>} - ID of the created game
 */
export const createSeasonGame = async (uid, playerId, seasonId, gameData) => {
  try {
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    
    // Log full incoming data structure
    console.log('Creating season game with full data:', {
      seasonId,
      gameData: {
        battingStats: gameData.battingStats,
        date: gameData.date,
        opponent: gameData.opponent
      }
    });
    
    // Process game data with strict number conversion
    const processedGameData = {
      ...gameData,
      battingStats: gameData.battingStats ? {
        atBats: Number(gameData.battingStats.atBats) || 0,
        hits: Number(gameData.battingStats.hits) || 0,
        doubles: Number(gameData.battingStats.doubles) || 0,
        triples: Number(gameData.battingStats.triples) || 0,
        homeRuns: Number(gameData.battingStats.homeRuns) || 0,
        runs: Number(gameData.battingStats.runs) || 0,
        rbi: Number(gameData.battingStats.rbi) || 0,
        walks: Number(gameData.battingStats.walks) || 0,
        strikeouts: Number(gameData.battingStats.strikeouts) || 0,
        stolenBases: Number(gameData.battingStats.stolenBases) || 0,
        hitByPitch: Number(gameData.battingStats.hitByPitch) || 0,
        sacrificeFlies: Number(gameData.battingStats.sacrificeFlies) || 0
      } : null,
      pitchingStats: gameData.pitchingStats ? {
        inningsPitched: Number(gameData.pitchingStats.inningsPitched) || 0,
        strikeouts: Number(gameData.pitchingStats.strikeouts) || 0,
        hits: Number(gameData.pitchingStats.hits) || 0,
        walks: Number(gameData.pitchingStats.walks) || 0,
        earnedRuns: Number(gameData.pitchingStats.earnedRuns) || 0,
        result: gameData.pitchingStats.result || '',
        ...(gameData.pitchingStats.pitchCount && { pitchCount: Number(gameData.pitchingStats.pitchCount) }),
        ...(gameData.pitchingStats.strikes && { strikes: Number(gameData.pitchingStats.strikes) }),
        ...(gameData.pitchingStats.balls && { balls: Number(gameData.pitchingStats.balls) }),
        ...(gameData.pitchingStats.wildPitches && { wildPitches: Number(gameData.pitchingStats.wildPitches) }),
        ...(gameData.pitchingStats.hitBatters && { hitBatters: Number(gameData.pitchingStats.hitBatters) }),
        ...(gameData.pitchingStats.balks && { balks: Number(gameData.pitchingStats.balks) }),
        ...(gameData.pitchingStats.pickoffs && { pickoffs: Number(gameData.pitchingStats.pickoffs) }),
        ...(gameData.pitchingStats.groundBallOuts && { groundBallOuts: Number(gameData.pitchingStats.groundBallOuts) }),
        ...(gameData.pitchingStats.flyBallOuts && { flyBallOuts: Number(gameData.pitchingStats.flyBallOuts) }),
        ...(gameData.pitchingStats.completedGame !== undefined && { completedGame: Boolean(gameData.pitchingStats.completedGame) }),
        ...(gameData.pitchingStats.shutout !== undefined && { shutout: Boolean(gameData.pitchingStats.shutout) })
      } : null,
      seasonId: seasonId, // Ensure seasonId is explicitly set
      date: gameData.date || new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Log processed data before saving
    console.log('Processed game data before save:', {
      seasonId: processedGameData.seasonId,
      battingStats: processedGameData.battingStats,
      date: processedGameData.date
    });
    
    const docRef = await addDoc(gameCollectionRef, processedGameData);
    
    // Verify the saved game
    const savedGame = await getDoc(docRef);
    console.log('Verified saved game data:', {
      id: docRef.id,
      data: savedGame.data()
    });
    
    // Get and log all games for this season
    const seasonGames = await getSeasonGames(uid, playerId, seasonId);
    
    // Log detailed season totals
    const totalAtBats = seasonGames.reduce((sum, game) => sum + Number(game.battingStats?.atBats || 0), 0);
    const totalHits = seasonGames.reduce((sum, game) => sum + Number(game.battingStats?.hits || 0), 0);
    
    console.log('Season totals after new game:', {
      seasonId,
      gamesCount: seasonGames.length,
      totalAtBats,
      totalHits,
      expectedAverage: totalAtBats > 0 ? (totalHits / totalAtBats).toFixed(3) : '0.000'
    });
    
    // Update season stats after creating game
    try {
      console.log('[STATS DEBUG] Updating stats after adding new game...');
      await updateSeasonPlayerStats(uid, playerId, seasonId);
      console.log('[STATS DEBUG] Successfully updated stats after creating new game');
    } catch (statsError) {
      console.error('[STATS DEBUG] Error updating stats after creating game:', statsError);
      // Don't throw here, we still want to return the game ID
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating game for season:', error);
    throw error;
  }
};

/**
 * Get all games for a player in a specific season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<Array>} - Array of game objects
 */
export const getSeasonGames = async (uid, playerId, seasonId) => {
  try {
    console.log('[STATS DEBUG] Getting season games with params:', { uid, playerId, seasonId });
    
    // First, get all games regardless of seasonId
    const allGamesRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const allGamesSnap = await getDocs(allGamesRef);
    
    console.log(`[STATS DEBUG] Total games in collection: ${allGamesSnap.size}`);
    
    // Create the season-specific query
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const q = query(
      gameCollectionRef,
      where('seasonId', '==', seasonId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const games = [];
    querySnapshot.forEach((doc) => {
      const gameData = { id: doc.id, ...doc.data() };
      
      // Ensure battingStats is properly formatted with numeric values
      if (gameData.battingStats) {
        gameData.battingStats = {
          atBats: Number(gameData.battingStats.atBats || 0),
          hits: Number(gameData.battingStats.hits || 0),
          doubles: Number(gameData.battingStats.doubles || 0),
          triples: Number(gameData.battingStats.triples || 0),
          homeRuns: Number(gameData.battingStats.homeRuns || 0),
          runs: Number(gameData.battingStats.runs || 0),
          rbi: Number(gameData.battingStats.rbi || 0),
          walks: Number(gameData.battingStats.walks || 0),
          strikeouts: Number(gameData.battingStats.strikeouts || 0),
          stolenBases: Number(gameData.battingStats.stolenBases || 0),
          hitByPitch: Number(gameData.battingStats.hitByPitch || 0),
          sacrificeFlies: Number(gameData.battingStats.sacrificeFlies || 0)
        };
      }
      
      games.push(gameData);
      console.log('[STATS DEBUG] Found game:', {
        id: doc.id,
        date: gameData.date,
        seasonId: gameData.seasonId,
        battingStats: gameData.battingStats ? {
          atBats: gameData.battingStats.atBats,
          hits: gameData.battingStats.hits
        } : 'No batting stats'
      });
    });
    
    console.log(`[STATS DEBUG] Total games found for season ${seasonId}:`, games.length);
    
    // Log total at-bats and hits for verification
    const totalAtBats = games.reduce((sum, game) => sum + Number(game.battingStats?.atBats || 0), 0);
    const totalHits = games.reduce((sum, game) => sum + Number(game.battingStats?.hits || 0), 0);
    console.log('[STATS DEBUG] Season totals:', {
      totalGames: games.length,
      totalAtBats,
      totalHits,
      expectedAverage: totalAtBats > 0 ? (totalHits / totalAtBats).toFixed(3) : '0.000'
    });
    
    return games;
  } catch (error) {
    console.error('[STATS DEBUG] Error getting season games:', error);
    throw error;
  }
};

/**
 * Update a game within a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} gameId - Game ID
 * @param {object} gameData - Game data to update
 * @returns {Promise<void>}
 */
export const updateSeasonGame = async (uid, playerId, gameId, gameData) => {
  try {
    const gameRef = doc(db, 'users', uid, 'players', playerId, 'games', gameId);
    const gameSnapshot = await getDoc(gameRef);
    
    if (!gameSnapshot.exists()) {
      throw new Error('Game not found');
    }
    
    const seasonId = gameSnapshot.data().seasonId;
    
    await updateDoc(gameRef, {
      ...gameData,
      updatedAt: serverTimestamp()
    });
    
    // After updating a game, update both season and career stats
    if (seasonId) {
      console.log(`[STATS DEBUG] Game updated, recalculating stats for season ${seasonId}`);
      try {
        await updateSeasonPlayerStats(uid, playerId, seasonId);
        console.log('[STATS DEBUG] Successfully updated stats after game update');
      } catch (statsError) {
        console.error('[STATS DEBUG] Error updating stats after game update:', statsError);
      }
    }
  } catch (error) {
    console.error('Error updating season game:', error);
    throw error;
  }
};

/**
 * Delete a game from a season
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} gameId - Game ID
 * @returns {Promise<void>}
 */
export const deleteSeasonGame = async (uid, playerId, gameId) => {
  try {
    const gameRef = doc(db, 'users', uid, 'players', playerId, 'games', gameId);
    const gameSnapshot = await getDoc(gameRef);
    
    if (!gameSnapshot.exists()) {
      throw new Error('Game not found');
    }
    
    const seasonId = gameSnapshot.data().seasonId;
    
    await deleteDoc(gameRef);
    
    // After deleting a game, update both season and career stats
    if (seasonId) {
      console.log(`[STATS DEBUG] Game deleted, recalculating stats for season ${seasonId}`);
      try {
        await updateSeasonPlayerStats(uid, playerId, seasonId);
        console.log('[STATS DEBUG] Successfully updated stats after game deletion');
      } catch (statsError) {
        console.error('[STATS DEBUG] Error updating stats after game deletion:', statsError);
      }
    }
  } catch (error) {
    console.error('Error deleting season game:', error);
    throw error;
  }
};

/**
 * Get season stats for a player
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<object>} - Season stats
 */
export const getSeasonStats = async (uid, playerId, seasonId) => {
  try {
    console.log(`[STATS DEBUG] Getting season stats for player ${playerId}, season ${seasonId}`);
    
    const statsRef = doc(db, 'users', uid, 'players', playerId, 'seasons', seasonId);
    const statsSnapshot = await getDoc(statsRef);
    
    if (statsSnapshot.exists()) {
      console.log(`[STATS DEBUG] Found existing stats for season ${seasonId}:`, {
        battingStats: statsSnapshot.data().battingStats ? {
          games: statsSnapshot.data().battingStats.games || 0,
          atBats: statsSnapshot.data().battingStats.atBats || 0,
          hits: statsSnapshot.data().battingStats.hits || 0,
          average: statsSnapshot.data().battingStats.average || 0
        } : 'No batting stats'
      });
      
      // Check if the stats are valid (have games > 0)
      if (!statsSnapshot.data().battingStats || 
          statsSnapshot.data().battingStats.games === 0) {
        
        console.log(`[STATS DEBUG] Stats exist but show 0 games, recalculating...`);
        
        // Get the season games to check if there are actually games
        const seasonGames = await getSeasonGames(uid, playerId, seasonId);
        
        if (seasonGames && seasonGames.length > 0) {
          console.log(`[STATS DEBUG] Found ${seasonGames.length} games for season ${seasonId}, but stats show 0 games. Recalculating...`);
          
          // Force recalculation of stats
          await updateSeasonPlayerStats(uid, playerId, seasonId);
          
          // Fetch the newly calculated stats
          const updatedStatsSnapshot = await getDoc(statsRef);
          return updatedStatsSnapshot.exists() ? updatedStatsSnapshot.data() : null;
        } else {
          console.log(`[STATS DEBUG] No games found for season ${seasonId}, stats are correct with 0 games.`);
        }
      }
      
      return statsSnapshot.data();
    } else {
      console.log(`[STATS DEBUG] No stats found for season ${seasonId}, calculating...`);
      
      // If stats don't exist yet, calculate them
      await updateSeasonPlayerStats(uid, playerId, seasonId);
      
      // Fetch the newly calculated stats
      const updatedStatsSnapshot = await getDoc(statsRef);
      
      if (updatedStatsSnapshot.exists()) {
        console.log(`[STATS DEBUG] Successfully calculated stats for season ${seasonId}:`, {
          battingStats: updatedStatsSnapshot.data().battingStats ? {
            games: updatedStatsSnapshot.data().battingStats.games || 0,
            atBats: updatedStatsSnapshot.data().battingStats.atBats || 0,
            hits: updatedStatsSnapshot.data().battingStats.hits || 0,
            average: updatedStatsSnapshot.data().battingStats.average || 0
          } : 'No batting stats'
        });
      } else {
        console.log(`[STATS DEBUG] Failed to calculate stats for season ${seasonId}`);
      }
      
      return updatedStatsSnapshot.exists() ? updatedStatsSnapshot.data() : null;
    }
  } catch (error) {
    console.error('[STATS DEBUG] Error getting season stats:', error);
    throw error;
  }
};

/**
 * Check for games without a seasonId and fix them
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} playerId - Player ID
 * @param {string} seasonId - Season ID to assign to games
 * @returns {Promise<number>} - Number of fixed games
 */
export const checkAndFixGamesWithoutSeason = async (uid, playerId, seasonId) => {
  try {
    console.log('Checking for games without seasonId...');
    
    // Get all games that don't have a seasonId
    const gameCollectionRef = collection(db, 'users', uid, 'players', playerId, 'games');
    const q = query(
      gameCollectionRef,
      where('seasonId', '==', null)
    );
    const querySnapshot = await getDocs(q);
    
    console.log(`Found ${querySnapshot.size} games without seasonId`);
    
    // Update each game with the current seasonId
    const batch = writeBatch(db);
    querySnapshot.forEach((doc) => {
      const gameRef = doc.ref;
      console.log('Updating game:', doc.id);
      batch.update(gameRef, { 
        seasonId: seasonId,
        updatedAt: serverTimestamp()
      });
    });
    
    if (querySnapshot.size > 0) {
      await batch.commit();
      console.log('Games updated with seasonId');
      
      // Recalculate season stats
      await updateSeasonPlayerStats(uid, playerId, seasonId);
      console.log('Season stats recalculated');
    }
    
    return querySnapshot.size;  // Return number of fixed games
  } catch (error) {
    console.error('Error fixing games without season:', error);
    throw error;
  }
};
